import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URL } from '../utils/fatch';
import styles from './sharing.module.css';

/**
 * Share a trip: invite people by email, see who is already on it, and (if you
 * own the trip) revoke invites or remove people.
 */
export function ShareTripPage() {
    const { id } = useParams();

    const [trip, setTrip] = useState(null);
    const [role, setRole] = useState(null);
    const [members, setMembers] = useState([]);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [inviteError, setInviteError] = useState('');
    const [lastInvite, setLastInvite] = useState(null);
    const [copiedLink, setCopiedLink] = useState('');

    const isOwner = role === 'owner';

    async function loadSharing() {
        try {
            const [sharingResponse, tripResponse] = await Promise.all([
                fetch(`${API_URL}/sharing/trip/${id}`, { credentials: 'include' }),
                fetch(`${API_URL}/tripTotalInfo/${id}`, { credentials: 'include' }),
            ]);

            const sharingData = await sharingResponse.json();

            if (!sharingResponse.ok) {
                setLoadError(sharingData.error || 'Could not load this trip');
                return;
            }

            setRole(sharingData.role);
            setMembers(sharingData.members);
            setPendingInvites(sharingData.pendingInvites);

            if (tripResponse.ok) {
                const tripData = await tripResponse.json();
                setTrip(tripData.trip);
            }
        } catch (error) {
            console.error('Error loading sharing info:', error);
            setLoadError('Could not reach the server');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadSharing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const sendInvite = async (event) => {
        event.preventDefault();
        if (sending || !email.trim()) return;

        setSending(true);
        setInviteError('');
        setLastInvite(null);

        try {
            const response = await fetch(`${API_URL}/sharing/invite`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tripId: Number(id), email: email.trim() }),
            });

            const data = await response.json();

            if (!response.ok) {
                setInviteError(data.error || 'Could not send the invite');
                return;
            }

            setLastInvite(data);
            setEmail('');
            loadSharing();
        } catch (error) {
            console.error('Error sending invite:', error);
            setInviteError('Could not reach the server');
        } finally {
            setSending(false);
        }
    };

    const copyLink = async (link) => {
        try {
            await navigator.clipboard.writeText(link);
            setCopiedLink(link);
            setTimeout(() => setCopiedLink(''), 2000);
        } catch (error) {
            console.error('Could not copy the link:', error);
        }
    };

    const revokeInvite = async (inviteId) => {
        try {
            const response = await fetch(`${API_URL}/sharing/invite/${inviteId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!response.ok) throw new Error(`Server returned ${response.status}`);
            loadSharing();
        } catch (error) {
            console.error('Could not revoke the invite:', error);
        }
    };

    const removeMember = async (userId, name) => {
        if (!window.confirm(`Remove ${name} from this trip?`)) return;

        try {
            const response = await fetch(`${API_URL}/sharing/member/${userId}?tripId=${id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!response.ok) throw new Error(`Server returned ${response.status}`);
            loadSharing();
        } catch (error) {
            console.error('Could not remove them:', error);
        }
    };

    if (loading) {
        return <div className={styles.page}><p className={styles.notice}>Loading…</p></div>;
    }

    if (loadError) {
        return (
            <div className={styles.page}>
                <p className={styles.notice}>{loadError}</p>
                <Link to="/all_trips" className={styles.backLink}>← Back to my trips</Link>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Link to="/all_trips" className={styles.backLink}>← Back to my trips</Link>

            <header className={styles.header}>
                {trip?.img && <div className={styles.headerImage} style={{ backgroundImage: `url(${trip.img})` }} />}
                <div>
                    <h1>Share {trip?.trip_name || 'this trip'}</h1>
                    <p>Everyone you add can see the budget and activities, add expenses, and use the packing assistant.</p>
                </div>
            </header>

            {isOwner ? (
                <section className={styles.card}>
                    <h2>Invite someone</h2>
                    <form className={styles.inviteForm} onSubmit={sendInvite}>
                        <input
                            type="email"
                            className={styles.emailInput}
                            placeholder="their@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={sending}
                            required
                        />
                        <button type="submit" className={styles.inviteBtn} disabled={sending || !email.trim()}>
                            {sending ? 'Sending…' : 'Send invite'}
                        </button>
                    </form>

                    <p className={styles.hint}>
                        They don&apos;t need an account yet — the email walks them through it. The invite only works for
                        the address you enter.
                    </p>

                    {inviteError && <p className={styles.error}>{inviteError}</p>}

                    {lastInvite && (
                        <div className={styles.sentBox}>
                            <p className={styles.sentTitle}>
                                {lastInvite.emailSent
                                    ? `Invite emailed to ${lastInvite.email} ✉️`
                                    : lastInvite.warning}
                            </p>
                            <div className={styles.linkRow}>
                                <code className={styles.linkText}>{lastInvite.link}</code>
                                <button type="button" className={styles.copyBtn} onClick={() => copyLink(lastInvite.link)}>
                                    {copiedLink === lastInvite.link ? 'Copied' : 'Copy link'}
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            ) : (
                <section className={styles.card}>
                    <p className={styles.hint}>
                        You were invited to this trip. Only the owner can add or remove people.
                    </p>
                </section>
            )}

            <section className={styles.card}>
                <h2>On this trip ({members.length})</h2>
                <div className={styles.memberList}>
                    {members.map((member) => (
                        <div key={member.user_id} className={styles.member}>
                            <div className={styles.avatar}>
                                {(member.firstname || member.email)[0].toUpperCase()}
                            </div>
                            <div className={styles.memberInfo}>
                                <span className={styles.memberName}>
                                    {member.firstname ? `${member.firstname} ${member.lastname || ''}`.trim() : member.email}
                                </span>
                                <span className={styles.memberEmail}>{member.email}</span>
                            </div>
                            <span className={member.role === 'owner' ? styles.ownerTag : styles.memberTag}>
                                {member.role === 'owner' ? 'Owner' : 'Collaborator'}
                            </span>
                            {isOwner && member.role !== 'owner' && (
                                <button
                                    type="button"
                                    className={styles.removeBtn}
                                    onClick={() => removeMember(member.user_id, member.firstname || member.email)}
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {pendingInvites.length > 0 && (
                <section className={styles.card}>
                    <h2>Waiting to join ({pendingInvites.length})</h2>
                    <div className={styles.memberList}>
                        {pendingInvites.map((invite) => (
                            <div key={invite.id} className={styles.member}>
                                <div className={`${styles.avatar} ${styles.avatarPending}`}>✉️</div>
                                <div className={styles.memberInfo}>
                                    <span className={styles.memberName}>{invite.email}</span>
                                    <span className={styles.memberEmail}>Invite sent, not accepted yet</span>
                                </div>
                                <button type="button" className={styles.copyBtn} onClick={() => copyLink(invite.link)}>
                                    {copiedLink === invite.link ? 'Copied' : 'Copy link'}
                                </button>
                                {isOwner && (
                                    <button type="button" className={styles.removeBtn} onClick={() => revokeInvite(invite.id)}>
                                        Revoke
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
